/**
 * Composite Analyzer: Confluence Composite — ATM Scope
 *
 * ATM-scope adaptation of confluenceComposite. Setup-scope asks "which
 * confluence PAIRS define this setup's edge?" — this asks the same thing
 * about the management style: "which entry confluence patterns does
 * THIS ATM actually make money on?"
 *
 * Why this is a distinct signal:
 *   A trader can have ONE setup that pairs with TWO different ATMs (one
 *   for fast scalps, one for runners). Confluence patterns that work
 *   with the scalp ATM may fail with the runner ATM — different
 *   exit math, different confluence requirements at entry.
 *
 *   The setup-scope analyzer mixes both ATMs together when computing
 *   pair edges. This one separates them so the trader can see, e.g.,
 *   "Volume Spike + Support" wins on the runner ATM (continuation) but
 *   "VWAP Reclaim + Bid Defense" wins on the scalp ATM (mean-revert).
 *
 * Patterns mirror the setup-scope analyzer:
 *   1. Winning confluence pairs on this ATM
 *   2. Cost of overriding counter-confluences on this ATM
 *   3. Confluences reserved for winners on this ATM
 *
 * Reuses the helpers (`buildLabelMap`, `confluencesOf`,
 * `counterConfluencesOf`) from confluenceComposite so the threshold
 * tuning + label resolution stays consistent across scopes.
 *
 * Reads:
 *   trade.confluences, trade.counterConfluences, trade.netPnl
 *   ctx.allConfluences (for label resolution)
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';
import {
  buildLabelMap,
  confluencesOf,
  counterConfluencesOf,
  PAIR_MIN_WINNERS,
  PAIR_MAX_LOSERS,
  PAIR_MIN_WR,
  OVERRIDE_MIN_TRADES,
  OVERRIDE_MIN_WR_GAP,
  RESERVED_MIN_WINNERS,
} from './confluenceComposite.js';

export const key = 'confluenceCompositeAtm';
export const tier = 'free';

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;
const labelFor = (id, labelMap) => labelMap.get(id) || id;

// ── Pattern 1: Winning pairs on this ATM ─────────────────────────────────

const detectAtmWinningPair = (atmName, trades, labelMap) => {
  const pairCount = new Map();
  for (const t of trades) {
    const conf = confluencesOf(t);
    if (conf.length < 2) continue;
    const win = isWin(t);
    for (let i = 0; i < conf.length - 1; i++) {
      for (let j = i + 1; j < conf.length; j++) {
        const k = `${conf[i]}|${conf[j]}`;
        if (!pairCount.has(k)) pairCount.set(k, { winners: 0, losers: 0, key: [conf[i], conf[j]] });
        const e = pairCount.get(k);
        if (win) e.winners++; else e.losers++;
      }
    }
  }
  if (pairCount.size === 0) return null;

  const candidates = [];
  for (const e of pairCount.values()) {
    const total = e.winners + e.losers;
    if (e.winners < PAIR_MIN_WINNERS) continue;
    if (e.losers > PAIR_MAX_LOSERS) continue;
    const wr = total > 0 ? e.winners / total : 0;
    if (wr < PAIR_MIN_WR) continue;
    candidates.push({ ...e, total, wr });
  }
  if (candidates.length === 0) return null;

  candidates.sort((a, b) => (b.wr - a.wr) || (b.total - a.total));
  const best = candidates[0];
  const labels = best.key.map(id => labelFor(id, labelMap));

  return makeObservation({
    id: `${key}:${atmName}:winning-pair:${best.key.join('-')}`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.CONFLUENCE,
    title: `"${atmName}" rewards entries with ${labels.join(' + ')} (${fmtPct(best.wr)} WR)`,
    detail: `${best.winners}/${best.total} winners on this ATM when these two confluences appeared together at entry. The ATM's exit logic gets paid when the entry has both signals — different ATMs may need different entry confluences.`,
    action: `When using this ATM, require ${labels.join(' + ')} at entry. Pair this ATM specifically with setups whose triggers include both.`,
    evidence: {
      pair: best.key, labels,
      winners: best.winners, losers: best.losers, wr: best.wr,
      candidatePairsConsidered: pairCount.size,
    },
    confidence: 0.78,
    priority: 76,
  });
};

// ── Pattern 2: Override cost on this ATM ─────────────────────────────────

const detectAtmOverrideCost = (atmName, trades, labelMap) => {
  const overrides = trades.filter(t => counterConfluencesOf(t).length > 0);
  const clean = trades.filter(t => counterConfluencesOf(t).length === 0);
  if (overrides.length < OVERRIDE_MIN_TRADES || clean.length < OVERRIDE_MIN_TRADES) return null;

  const wr = (group) => group.filter(isWin).length / group.length;
  const overrideWr = wr(overrides);
  const cleanWr = wr(clean);
  const gap = cleanWr - overrideWr;
  if (gap < OVERRIDE_MIN_WR_GAP) return null;

  const counterFreq = new Map();
  for (const t of overrides) {
    for (const cid of counterConfluencesOf(t)) counterFreq.set(cid, (counterFreq.get(cid) || 0) + 1);
  }
  const sortedCounters = [...counterFreq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3)
    .map(([id, n]) => `${labelFor(id, labelMap)} (${n}×)`);
  const overrideNet = overrides.reduce((s, t) => s + (Number.isFinite(t.netPnl) ? t.netPnl : 0), 0);

  return makeObservation({
    id: `${key}:${atmName}:override-cost`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `"${atmName}" — overriding warnings cost ${fmtPct(gap)} of WR`,
    detail: `${overrides.length} trades on this ATM taken DESPITE counter-confluences: ${fmtPct(overrideWr)} WR, net ${overrideNet >= 0 ? '+' : ''}$${Math.round(overrideNet).toLocaleString()}. Same ATM with no counter flagged: ${fmtPct(cleanWr)} WR. Most-overridden warnings: ${sortedCounters.join(', ')}.`,
    action: `This ATM amplifies bad entries — its trail/BE/target geometry can't recover from the structural warnings you're overriding. Treat counter-confluences as a hard pass when running this ATM.`,
    evidence: {
      overrideCount: overrides.length, cleanCount: clean.length,
      overrideWr, cleanWr, gap, overrideNet,
      topCounters: [...counterFreq.entries()].slice(0, 5),
    },
    confidence: 0.8,
    priority: 80,
  });
};

// ── Pattern 3: Reserved confluences on this ATM ──────────────────────────

const detectAtmReservedConfluences = (atmName, trades, labelMap) => {
  const winners = trades.filter(isWin);
  const losers = trades.filter(t => Number.isFinite(t.netPnl) && t.netPnl <= 0);
  if (winners.length < RESERVED_MIN_WINNERS || losers.length < 2) return null;

  const winFreq = new Map();
  for (const t of winners) {
    for (const cid of confluencesOf(t)) winFreq.set(cid, (winFreq.get(cid) || 0) + 1);
  }
  const loseSet = new Set();
  for (const t of losers) {
    for (const cid of confluencesOf(t)) loseSet.add(cid);
  }

  const reserved = [];
  for (const [cid, count] of winFreq) {
    if (count < RESERVED_MIN_WINNERS) continue;
    if (loseSet.has(cid)) continue;
    reserved.push({ id: cid, winnerCount: count, label: labelFor(cid, labelMap) });
  }
  if (reserved.length === 0) return null;

  reserved.sort((a, b) => b.winnerCount - a.winnerCount);
  const top = reserved.slice(0, 3);
  const labels = top.map(r => `${r.label} (${r.winnerCount}/${winners.length} winners)`);

  return makeObservation({
    id: `${key}:${atmName}:reserved-${top.map(r => r.id).join('-')}`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.CONFLUENCE,
    title: `"${atmName}" — ${top.length === 1 ? 'a confluence is' : 'confluences are'} reserved for winners only`,
    detail: `${labels.join(', ')}. ${top.length === 1 ? 'This confluence appears' : 'These confluences each appear'} in this ATM's winners but never in its losers — the ATM's exit logic only gets paid when the entry has the signal.`,
    action: `Make ${top.length === 1 ? 'this confluence' : 'one of these confluences'} a required filter when running this ATM. The data says you don't lose with it present.`,
    evidence: { reserved: top, winnerCount: winners.length, loserCount: losers.length },
    confidence: 0.78,
    priority: 76,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { atmName, atmTrades, allConfluences = [] } = ctx || {};
  if (!atmName || !Array.isArray(atmTrades) || atmTrades.length === 0) return null;

  // Live-trade gate: winning-pair mining, override-cost gap, and
  // reserved-confluence detection all bucket trades by winner vs loser.
  // Open trades have unrealized P&L which isn't a realized outcome —
  // including them would fire premature "winning recipe" / "override is
  // bleeding" claims while the user is still in the trade (panic-exit
  // risk). Matches summarizePnl semantics.
  const closedAtmTrades = atmTrades.filter(t => t?.isOpen !== true);
  if (closedAtmTrades.length === 0) return null;

  const labelMap = buildLabelMap(allConfluences);
  const out = [];

  const pair = detectAtmWinningPair(atmName, closedAtmTrades, labelMap);
  if (pair) out.push(pair);
  const override = detectAtmOverrideCost(atmName, closedAtmTrades, labelMap);
  if (override) out.push(override);
  const reserved = detectAtmReservedConfluences(atmName, closedAtmTrades, labelMap);
  if (reserved) out.push(reserved);

  return out.length === 0 ? null : out;
};

// Exports for tests
export { detectAtmWinningPair, detectAtmOverrideCost, detectAtmReservedConfluences };
